import { CreditCardOutlined } from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Button, Flex, Form, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShowInfoGrid, ShowInfoItem } from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection, ShowSubsection } from '@/components/general/ShowInfo/ShowSection';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';

import ServiceOrderAdminData from '../ServiceOrderForm/ServiceOrderAdminData';
import { calculateServiceOrderTotal, formatWithSuffix } from './serviceOrderShowHelpers';
import useInlineSectionEdit from './useInlineSectionEdit';

interface ServiceOrderShowAdminProps {
  serviceOrder: ServiceOrder;
  refetch?: () => Promise<any>;
  modifingAdminData?: boolean;
  setModifingAdminData?: (modifying: boolean) => void;
  blockEdit: boolean;
}

const ServiceOrderShowAdmin = (props: ServiceOrderShowAdminProps) => {
  const { userWorkArea } = useUserContext();
  const { t } = useTranslation();
  const {
    serviceOrder,
    refetch,
    modifingAdminData = false,
    setModifingAdminData,
    blockEdit,
  } = props;
  const {
    kilometres_estimated,
    hours_estimated,
    kilometres_covered,
    hours_covered,
    kilometres_rate,
    hourly_rate,
    installation_price,
    currency,
    dollar_quote_used,
    technician_payment_ars,
  } = serviceOrder;
  const { formProps, isSaving, startEditing } = useInlineSectionEdit({
    id: serviceOrder.id,
    record: serviceOrder,
    isEditing: modifingAdminData,
    setIsEditing: setModifingAdminData ?? (() => undefined),
    refetch: refetch ?? (() => Promise.resolve()),
    successMessage: t('Orden de servicio actualizada correctamente'),
  });
  const [saveEdit, setSaveEdit] = useState<boolean>(false);
  // Kilómetros y horas netos a facturar: lo indicado menos lo ya cubierto por Acronex.
  const kilometresToInvoice =
    kilometres_estimated !== undefined
      ? kilometres_estimated - (kilometres_covered ?? 0)
      : undefined;
  const hoursToInvoice =
    hours_estimated !== undefined ? hours_estimated - (hours_covered ?? 0) : undefined;
  const total = calculateServiceOrderTotal({
    hours_estimated,
    kilometres_estimated,
    hours_covered,
    kilometres_covered,
    hourly_rate,
    kilometres_rate,
    installation_price,
  });
  const canLoadInvoices =
    !blockEdit &&
    !isSaving &&
    serviceOrder.status !== SERVICE_ORDER_STATUS.FINALIZADA &&
    (userWorkArea === USER_WORK_AREA.ADMINISTRATION || userWorkArea === USER_WORK_AREA.SOFTWARE) &&
    serviceOrder.technician_confirmed === true;
  // Normaliza los números de factura persistidos para mostrarlos como lista.
  const renderBillingNumbers = (value?: string) => {
    const billingNumbers = value
      ?.split(',')
      .map((number: string) => number.trim())
      .filter(Boolean);

    return billingNumbers?.length
      ? billingNumbers.map(number => <li key={number}>{number}</li>)
      : '-';
  };

  return (
    <ShowSection title={t('Detalles de administración')} className="ServiceOrderShowAdmin">
      {!saveEdit && (
        <>
          <ShowSubsection title={t('Detalles a facturar')}>
            <ShowInfoGrid>
              {installation_price !== undefined ? (
                <ShowInfoItem label={t('Precio de la instalación')}>
                  <TextField
                    value={formatWithSuffix(installation_price, currency?.simbolo ?? '')}
                  />
                </ShowInfoItem>
              ) : (
                <ShowInfoItem label={t('Horas a facturar')}>
                  <TextField value={formatWithSuffix(hoursToInvoice, 'hs')} />
                </ShowInfoItem>
              )}
              <ShowInfoItem label={t('Kilómetros a facturar')}>
                <TextField value={formatWithSuffix(kilometresToInvoice, 'km')} />
              </ShowInfoItem>
            </ShowInfoGrid>

            <ShowInfoItem
              label={`${t('TOTAL POR HORAS Y KILÓMETROS')} (${t('sin IVA')})`}
              className="ServiceOrderShowAdminTotal">
              <TextField
                value={
                  total !== undefined && currency !== undefined
                    ? formatWithSuffix(total, currency?.simbolo)
                    : '-'
                }
              />
            </ShowInfoItem>

            {dollar_quote_used != null && technician_payment_ars != null && (
              <ShowSubsection title={t('Conversión a pesos')}>
                <ShowInfoGrid>
                  <ShowInfoItem label={t('Cotización USD (día del servicio)')}>
                    <TextField value={formatWithSuffix(dollar_quote_used, '$')} />
                  </ShowInfoItem>
                  <ShowInfoItem label={t('Total en pesos (ARS)')}>
                    <TextField value={formatWithSuffix(technician_payment_ars, '$')} />
                  </ShowInfoItem>
                </ShowInfoGrid>
              </ShowSubsection>
            )}
          </ShowSubsection>

          <ShowSubsection title={t('Facturación')}>
            {modifingAdminData ? (
              /* Modo carga: solo el input de facturas es editable */
              <Form
                {...formProps}
                initialValues={serviceOrder}
                onFinish={v => {
                  setSaveEdit(true);
                  formProps?.onFinish?.(v);
                  setModifingAdminData?.(false);
                }}
                layout="vertical">
                <ServiceOrderAdminData formProps={formProps} />
                <Flex justify="end" align="flex-end">
                  <Button onClick={() => setModifingAdminData?.(false)} style={{ marginRight: 8 }}>
                    {t('Cancelar')}
                  </Button>
                  <Button htmlType="submit" type="primary">
                    {t('Guardar')}
                  </Button>
                </Flex>
              </Form>
            ) : (
              /* Modo vista: facturas como lista + botón */
              <Flex justify="space-between" style={{ marginTop: 16 }} align="end">
                <ShowInfoGrid>
                  <ShowInfoItem label={t('Números de factura del técnico')}>
                    {serviceOrder.billing_numbers ? (
                      <ul>{renderBillingNumbers(serviceOrder.billing_numbers)}</ul>
                    ) : (
                      <TextField value={t('No hay pago al técnico')} />
                    )}
                  </ShowInfoItem>
                  <ShowInfoItem label={t('Números de factura del cliente')}>
                    {serviceOrder.customer_billing_numbers ? (
                      <ul>{renderBillingNumbers(serviceOrder.customer_billing_numbers)}</ul>
                    ) : (
                      <TextField value={t('No hay facturas al cliente')} />
                    )}
                  </ShowInfoItem>
                </ShowInfoGrid>
                <Tooltip
                  title={
                    !canLoadInvoices
                      ? t('Disponible cuando la respuesta del técnico es Conforme')
                      : undefined
                  }>
                  <Button
                    icon={<CreditCardOutlined />}
                    disabled={!canLoadInvoices}
                    loading={isSaving}
                    onClick={startEditing}>
                    {t('Cargar facturas')}
                  </Button>
                </Tooltip>
              </Flex>
            )}
          </ShowSubsection>
        </>
      )}
    </ShowSection>
  );
};

export default ServiceOrderShowAdmin;
